import { useEffect, useRef, useState } from 'react';
import { useSettingsStore, type WhoMergeMap } from '@/lib/settings-store';

/** Shared merge-undo-toast state machine (rule #30 layer 4). Extracted on the
 *  second consumer (PersonView + Garden grid, manual-merge arc M2; rule #4).
 *  Carries BOTH names so the toast copy is correct under a keeper flip, and the
 *  pre-merge map SNAPSHOT so Undo restores the exact prior state even when the
 *  merge rewrote transitive chains (a per-alias unmerge could not). 5s
 *  auto-dismiss with pause-on-hover/focus (latest-wins; a new merge replaces the
 *  toast + its timer). The presentational toast lives in `<MergeUndoToast>`;
 *  this hook is intentionally a NON-component file so the two can be imported
 *  without tripping `react-refresh/only-export-components`. */

export interface MergeToastState {
  absorbed: string;
  kept: string;
  prevMap: WhoMergeMap;
}

const DISMISS_MS = 5000;

export function useMergeUndoToast() {
  const setWhoMergeMap = useSettingsStore((s) => s.setWhoMergeMap);
  const [toast, setToast] = useState<MergeToastState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDismiss = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const scheduleDismiss = () => {
    cancelDismiss();
    timerRef.current = setTimeout(() => {
      setToast(null);
      timerRef.current = null;
    }, DISMISS_MS);
  };

  const showMergeToast = (absorbed: string, kept: string, prevMap: WhoMergeMap) => {
    setToast({ absorbed, kept, prevMap });
    scheduleDismiss();
  };

  const undo = () => {
    if (!toast) return;
    setWhoMergeMap(toast.prevMap); // restore the exact pre-merge snapshot
    setToast(null);
    cancelDismiss();
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return { toast, showMergeToast, undo, scheduleDismiss, cancelDismiss };
}
